import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Store, ChevronRight } from 'lucide-react';
import { sellersAPI } from '../../services/api';
import { SellerStatusBadge, SELLER_STATUS } from '../seller/SellerStatus';
import AdminSellerDetail from './AdminSellerDetail';

const FILTERS = ['', 'pending', 'under_review', 'draft', 'approved', 'rejected', 'suspended'];

// Admin : candidatures et comptes vendeurs (liste + fiche)
const AdminSellersPanel = () => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('pending');
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => sellersAPI.adminList({ status: status || undefined, q: q || undefined }).then(setData).catch(() => setData({ sellers: [], counts: {} })), [status, q]);
  useEffect(() => { load(); }, [load]);
  const openDetail = async (id) => setDetail(await sellersAPI.adminGet(id));
  const refreshDetail = async () => { if (detail) setDetail(await sellersAPI.adminGet(detail.seller.id)); load(); };

  if (detail) return <AdminSellerDetail data={detail} onBack={() => { setDetail(null); load(); }} onChanged={refreshDetail} />;
  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-orange-500" /></div>;
  const total = Object.values(data.counts || {}).reduce((a, b) => a + b, 0);

  return (
    <div data-testid="admin-sellers-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Store size={20} className="text-orange-500" /> Vendeurs professionnels</h2>
          <p className="text-sm text-gray-500">{total} candidature(s) · {data.counts?.pending || 0} à examiner · {data.counts?.approved || 0} approuvé(s)</p>
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (raison sociale, SIRET, e-mail)" className="h-10 rounded-md border border-gray-200 px-3 text-sm w-full sm:w-80" data-testid="admin-sellers-search" />
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => (
          <button key={f || 'all'} type="button" onClick={() => setStatus(f)} className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${status === f ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`} data-testid={`admin-sellers-filter-${f || 'all'}`}>
            {f ? SELLER_STATUS[f].label : 'Tous'} {f ? `(${data.counts?.[f] || 0})` : `(${total})`}
          </button>
        ))}
      </div>
      {!data.sellers.length ? (
        <p className="text-sm text-gray-500 bg-white rounded-xl shadow-sm p-8 text-center" data-testid="admin-sellers-empty">Aucune candidature dans ce filtre.</p>
      ) : (
        <ul className="bg-white rounded-xl shadow-sm divide-y divide-gray-100" data-testid="admin-sellers-list">
          {data.sellers.map((s) => (
            <li key={s.id}>
              <button type="button" onClick={() => openDetail(s.id)} className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-gray-50" data-testid={`admin-seller-row-${s.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{s.company_name || <span className="text-gray-400">Candidature sans nom</span>}</p>
                  <p className="text-xs text-gray-500 truncate">{s.activity_label || '—'} · SIRET {s.siret || '—'} · {s.contact?.email || '—'}</p>
                </div>
                <div className="hidden sm:block text-xs text-gray-500">{s.submitted_at ? `Soumise ${new Date(s.submitted_at).toLocaleDateString('fr-FR')}` : `Créée ${new Date(s.created_at).toLocaleDateString('fr-FR')}`}</div>
                <SellerStatusBadge status={s.status} />
                <ChevronRight size={16} className="text-gray-400" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AdminSellersPanel;
